/**
 * Which views are tabs, and where a tab goes. Apart from `ViewTabs.tsx` so that file only exports
 * components (the `react-refresh/only-export-components` rule), and the mobile select and the page
 * can use the same list.
 */
import type { ViewDef, ViewsResponse } from '../query/types'
import { DEFAULT_VIEW, withListChanges } from '../query/urlState'

/**
 * The tabs of an object: the standard views, the pinned saved views in pin order, and the open
 * saved view when it is not pinned (it stays as the last tab for as long as it is open). A pin on a
 * view the user may no longer see is skipped.
 */
export function tabViews(data: ViewsResponse, currentKey: string): ViewDef[] {
  const byKey = new Map(data.views.map((view) => [view.key, view]))
  const pinned = data.pins.flatMap((id) => byKey.get(id) ?? [])
  const open = pinned.some((view) => view.key === currentKey) ? undefined : byKey.get(currentKey)
  return [...data.system, ...pinned, ...(open ? [open] : [])]
}

/**
 * The parameters after choosing a view: its key (none for the default view) with the filter, sort
 * and columns of the previous one gone and the page back to 1. The search term stays, as in C's tabs.
 */
export function viewSearch(params: URLSearchParams, key: string): URLSearchParams {
  return withListChanges(params, { view: key === DEFAULT_VIEW ? null : key, f: null, sort: null, cols: null })
}

/** The name of a view on a tab or in a list: a view shared with the team says so. */
export function viewLabel(view: ViewDef): string {
  return view.visibility === 'team' ? `${view.name} (team)` : view.name
}
